export class InvalidColorError extends Error {}

export class InvalidHexColorError extends InvalidColorError {}

export class InvalidRgbColorError extends InvalidColorError {}

const hexRegex =
  /^#?([a-f\d]{1,2})([a-f\d]{1,2})([a-f\d]{1,2})([a-f\d]{1,2})?$/i;
const rgbaRegex =
  /^rgba?\(\s*([01]?\d\d?|2[0-4]\d|25[0-5])\s*,\s*([01]?\d\d?|2[0-4]\d|25[0-5])\s*,\s*([01]?\d\d?|2[0-4]\d|25[0-5])\s*(?:,\s*(1|0(?:\.\d+)?)\s*)?\)$/;

/**
 *
 * @param {string} hexColor
 * @returns {[number, number, number, ?number]}
 */
export function hex2rgba(hexColor) {
  const matches = hexColor.match(hexRegex);

  if (!matches) {
    throw new InvalidHexColorError('The provided color to convert is invalid.');
  }

  const [, ...rgba] = matches;

  return rgba.reduce((acc, color) => {
    if (color) {
      acc.push(parseInt(color.padEnd(2, color.charAt(0)), 16));
    }

    return acc;
  }, []);
}

/**
 *
 * @param {string|[number,number,number]} color      The color to test
 * @param {*}                             lightColor The value to return in case of light color
 * @param {*}                             darkColor  The value to return in case of dark color
 * @param {number}                        threshold  Must be between 0 and 255. Defaults to 125
 * @returns {*}
 * @see https://www.w3.org/TR/AERT/#color-contrast
 */
export function colorContrast(color, lightColor, darkColor, threshold = 125) {
  const [red, green, blue] =
    color instanceof Array ? color : colorToArray(color);

  const o = Math.round((red * 299 + green * 587 + blue * 114) / 1000);

  return o > threshold ? darkColor : lightColor;
}

/**
 *
 * @param {string} color
 * @return {[number, number, number, number|null]}
 */
export function colorToArray(color) {
  switch (color.charAt(0)) {
    case '#':
      return hex2rgba(color);

    case 'r':
      return rgbToArray(color);

    default:
      throw new InvalidColorError('The provided color is invalid.');
  }
}

/**
 *
 * @param {string} rgbaColor
 * @param {boolean} convertAlphaToNumber
 * @return {[number, number, number, number|null]}
 */
export function rgbToArray(rgbaColor, convertAlphaToNumber = false) {
  const matches = rgbaColor.match(rgbaRegex);

  if (!matches) {
    throw new InvalidRgbColorError('The provided color to convert is invalid.');
  }

  const [, red, green, blue, alpha = 1] = matches;

  return [
    parseInt(red, 10),
    parseInt(green, 10),
    parseInt(blue, 10),
    convertAlphaToNumber
      ? alphaFloatToNumber(parseInt(alpha, 10))
      : parseInt(alpha, 10),
  ];
}

/**
 *
 * @param {float|number} value
 * @return {number}
 */
export function alphaFloatToNumber(value) {
  return value === 0 ? 0 : 255 / value;
}

/**
 *
 * @param {number} value
 * @return {number}
 */
export function alphaNumberToFloat(value) {
  return value / 255;
}
